/**
 * 禁止文字（U+10000以上のUnicode、記号、約物など）を含むか判定する関数
 * @param str 判定対象文字列
 * @returns 禁止文字を含む場合true
 */
export function containsForbiddenUnicode(str: string): boolean {
  // 絵文字・サロゲートペア（U+10000以上）・環境依存文字を検出
  if (/\p{Extended_Pictographic}/u.test(str)) return true;
  if (/[\u{10000}-\u{10FFFF}]/u.test(str)) return true;
  // ギリシャ文字 (U+0370-U+03FF)
  if (/[\u0370-\u03FF]/u.test(str)) return true;
  // キリル文字 (U+0400-U+04FF)
  if (/[\u0400-\u04FF]/u.test(str)) return true;
  // ASCII記号・全角記号・Unicode記号・句読点を検出
  // ASCII: ! " # $ % & ' ( ) * + , - . / : ; < = > ? @ [ \ ] ^ _ ` { | } ~
  // 全角: ！ " ＃ ＄ ％ ＆ ’ （ ） ＊ ＋ ， － ． ／ ： ； ＜ ＝ ＞ ？ ＠ ［ ￥ ］ ＾ ＿ ‘ ｛ ｜ ｝ ～
  // Unicode記号・句読点: \p{S}（記号）, \p{P}（句読点）
  return /[!-/:-@[-`{-~！-／：-＠［-｀｛-～]/.test(str) || /[\p{S}\p{P}]/u.test(str);
}

/**
 * 全角カタカナのみか判定
 * @param str
 * @returns
 */
export function isFullWidthKatakanaOnly(str: string): boolean {
  // 未入力は許容
  if (!str) return true;
  // 全角カタカナ・長音のみ許容
  return /^[ァ-ヶー]+$/.test(str);
}

/**
 * 共通バリデーションwatcher
 * @param values 監視するref配列
 * @param errorRef エラー格納ref
 * @param message エラーメッセージ
 */
export function useValidationWatcher(
  values: [Ref<string>, Ref<string>],
  errorRef: Ref<string>,
  message: string,
  options?: { katakanaOnly?: boolean }
) {
  watch(values, ([v1, v2]) => {
    // スペースだけ（全角・半角いずれか）ならエラー
    if ((v1 && v1.trim().length === 0) || (v2 && v2.trim().length === 0)) {
      errorRef.value = "スペース（空白）のみの入力はできません";
      return;
    }
    if (containsForbiddenUnicode(v1) || containsForbiddenUnicode(v2)) {
      errorRef.value = `入力欄に環境依存文字があり読み取ることができません。<br>${message}`;
      return;
    }
    if (options?.katakanaOnly) {
      if ((v1 && !isFullWidthKatakanaOnly(v1)) || (v2 && !isFullWidthKatakanaOnly(v2))) {
        errorRef.value = "全角カタカナでご入力ください";
        return;
      }
    }
    errorRef.value = '';
  });
}

/**
 * メールアドレスバリデーション
 * @param email メールアドレス文字列
 * @returns エラーメッセージ（問題なければ空文字）
 */
export function validateEmail(email: string): string {
  if (!email) return '';
  // @で分割
  const parts = email.split('@');
  if (parts.length !== 2) {
    return "有効なメールアドレスではありません。<br>正しい形式でご入力ください";
  }
  const [local, domain] = parts;
  // ローカルパート64文字以上
  if (local.length === 0 || local.length > 64) {
    return "有効なメールアドレスではありません。<br>正しい形式でご入力ください";
  }
  // ドメインパートにドットが含まれていること
  if (!domain.includes('.')) {
    return "有効なメールアドレスではありません。<br>正しい形式でご入力ください";
  }
  // ドメインの各ラベルの先頭または末尾がハイフンはNG
  const domainLabels = domain.split('.');
  if (domainLabels.some(label => label.startsWith('-') || label.endsWith('-'))) {
    return "有効なメールアドレスではありません。<br>正しい形式でご入力ください";
  }
  // 許可記号以外が含まれている場合
  if (/[^a-zA-Z0-9\-_.+?\/@]/.test(email)) {
    return "有効なメールアドレスではありません。<br>正しい形式でご入力ください";
  }
  // .の連続使用
  if (local.includes('..') || domain.includes('..')) {
    return "有効なメールアドレスではありません。<br>正しい形式でご入力ください";
  }
  // .が最初・最後・@直前
  if (
    local.startsWith('.') ||
    local.endsWith('.') ||
    domain.startsWith('.') ||
    domain.endsWith('.') ||
    local.endsWith('@') ||
    local.endsWith('.')
  ) {
    return "有効なメールアドレスではありません。<br>正しい形式でご入力ください";
  }
  // RFC準拠の正規表現（簡易版）
  const rfcRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
  if (!rfcRegex.test(email)) {
    return "有効なメールアドレスではありません。<br>正しい形式でご入力ください";
  }
  return '';
}
